// Pure pairing & scheduling engine — no I/O, unit-testable. Every function
// returns plain data that feeds the EXISTING tee-sheet model: groups become
// teams-in-flights, pairs become opponentId links, and times/holes become wave
// assignments. Players are `{ id, name, handicap }` (handicap optional).

// Deterministic shuffle (seedable) so random pairings are reproducible in tests.
function mulberry32(seed) {
  let a = seed >>> 0
  return () => {
    a |= 0; a = (a + 0x6D2B79F5) | 0
    let t = Math.imul(a ^ (a >>> 15), 1 | a)
    t = (t + Math.imul(t ^ (t >>> 7), 61 | t)) ^ t
    return ((t ^ (t >>> 14)) >>> 0) / 4294967296
  }
}
function shuffled(arr, rng) {
  const a = arr.slice()
  for (let i = a.length - 1; i > 0; i--) {
    const j = Math.floor(rng() * (i + 1))
    ;[a[i], a[j]] = [a[j], a[i]]
  }
  return a
}
const idsOf = (players) => players.map((p) => (typeof p === 'string' ? p : p.id))
const chunk = (arr, size) => {
  const out = []
  for (let i = 0; i < arr.length; i += size) out.push(arr.slice(i, i + size))
  return out
}

// 1a. RANDOM groups of `size`. `seed` (number) makes it reproducible.
export function groupsRandom(players, size = 4, seed = 1) {
  return chunk(shuffled(idsOf(players), mulberry32(seed)), Math.max(1, size))
}

// 1b. BY HANDICAP — SNAKE (serpentine) into `flightCount` buckets: 0,1,2,…,n-1,
// n-1,…,1,0,… so each flight is evenly balanced by handicap.
export function flightsSnake(players, flightCount) {
  const sorted = [...players].sort((a, b) => (a.handicap ?? 99) - (b.handicap ?? 99))
  const buckets = Array.from({ length: Math.max(1, flightCount) }, () => [])
  let dir = 1, idx = 0
  for (const p of sorted) {
    buckets[idx].push(p.id ?? p)
    if (idx + dir < 0 || idx + dir >= buckets.length) dir = -dir
    else idx += dir
  }
  return buckets
}

// 1c. BY HANDICAP — SPLIT into contiguous blocks (low flight, high flight…).
export function flightsSplit(players, flightCount) {
  const sorted = [...players].sort((a, b) => (a.handicap ?? 99) - (b.handicap ?? 99))
  const per = Math.ceil(sorted.length / Math.max(1, flightCount))
  return chunk(sorted.map((p) => p.id ?? p), per)
}

// 2. A/B/C/D balanced groups: tier players into `groupSize` quartiles by
// handicap, then draw one from each tier into every group → each foursome has
// one A, one B, one C, one D.
export function groupsABCD(players, groupSize = 4) {
  const sorted = [...players].sort((a, b) => (a.handicap ?? 99) - (b.handicap ?? 99))
  const n = sorted.length
  const groupCount = Math.ceil(n / groupSize)
  const tiers = Array.from({ length: groupSize }, (_, t) => sorted.slice(t * groupCount, (t + 1) * groupCount))
  const groups = Array.from({ length: groupCount }, () => [])
  for (let t = 0; t < groupSize; t++) {
    for (let g = 0; g < groupCount; g++) {
      const p = tiers[t][g]
      if (p) groups[g].push(p.id ?? p)
    }
  }
  return groups.filter((g) => g.length > 0)
}

// 3a. SEEDED 1↔N pairing (best vs worst). ids in seed order.
export function pairSeeded(ids) {
  const a = ids.slice()
  const pairs = []
  while (a.length >= 2) pairs.push([a.shift(), a.pop()])
  if (a.length) pairs.push([a[0], null])
  return pairs
}

// 3b. RE-PAIR BY STANDING for the next round. 'adjacent' = 1-2, 3-4 (leaders
// play leaders); 'seeded' = 1↔N. rankedIds is current-standing order.
export function repairByStanding(rankedIds, method = 'adjacent') {
  if (method === 'seeded') return pairSeeded(rankedIds)
  const pairs = []
  for (let i = 0; i < rankedIds.length; i += 2) pairs.push([rankedIds[i], rankedIds[i + 1] ?? null])
  return pairs
}

// 4. KEEP-TOGETHER / KEEP-APART. together: groups of ids forced together;
// apart: pairs of ids forced into different groups. Repacks a grouping to honor
// both. Returns new groups of ≤ maxSize.
export function applyConstraints(players, maxSize, { together = [], apart = [], seed = 1 } = {}) {
  const ids = idsOf(players)
  const apartSet = apart.map((p) => new Set(p))
  // Build "clusters": start each id alone, then merge together-sets.
  const clusterOf = new Map(ids.map((id) => [id, id]))
  const members = new Map(ids.map((id) => [id, [id]]))
  const merge = (x, y) => {
    const cx = clusterOf.get(x), cy = clusterOf.get(y)
    if (cx === cy) return
    const list = members.get(cy)
    for (const m of list) { clusterOf.set(m, cx); members.get(cx).push(m) }
    members.delete(cy)
  }
  for (const grp of together) for (let i = 1; i < grp.length; i++) if (clusterOf.has(grp[0]) && clusterOf.has(grp[i])) merge(grp[0], grp[i])
  const clusters = shuffled([...members.values()], mulberry32(seed)).sort((a, b) => b.length - a.length)

  // Greedy bin-pack clusters into groups of ≤ maxSize, respecting apart.
  const groups = []
  const violatesApart = (group, cluster) => {
    const all = new Set([...group, ...cluster])
    return apartSet.some((pair) => [...pair].every((id) => all.has(id)))
  }
  for (const cluster of clusters) {
    let placed = false
    for (const g of groups) {
      if (g.length + cluster.length <= maxSize && !violatesApart(g, cluster)) { g.push(...cluster); placed = true; break }
    }
    if (!placed) groups.push([...cluster])
  }
  return groups
}

// 5. ROUND-ROBIN schedule (circle method): every id plays every other exactly
// once across (n-1) rounds (odd n → a bye per round). Returns rounds of pairs.
export function roundRobin(ids) {
  const arr = ids.slice()
  if (arr.length % 2 === 1) arr.push(null) // bye marker
  const n = arr.length
  const rounds = []
  const fixed = arr[0]
  let rot = arr.slice(1)
  for (let r = 0; r < n - 1; r++) {
    const line = [fixed, ...rot]
    const pairs = []
    for (let i = 0; i < n / 2; i++) pairs.push([line[i], line[n - 1 - i]])
    rounds.push(pairs)
    rot = [rot[rot.length - 1], ...rot.slice(0, rot.length - 1)] // rotate
  }
  return rounds
}

// 6. SEQUENTIAL tee times with interval + MULTIPLE STARTING TEES. groups in
// order; `tees` groups go off at the SAME slot (e.g. two-tee start: hole 1 and
// hole 10), then the clock advances by `intervalMin`. Returns
// [{ group, time, startHole }]. startHoles defaults to [1,10,…] per tee count.
export function sequentialTimes(groups, startTime, intervalMin, tees = 1, startHoles = null) {
  const [hStr, mStr] = String(startTime || '').split(':')
  const baseH = Number(hStr), baseM = Number(mStr)
  const step = Math.max(1, Number(intervalMin) || 0)
  const t = Math.max(1, Number(tees) || 1)
  const holes = startHoles || (t === 2 ? [1, 10] : t === 4 ? [1, 5, 10, 14] : Array.from({ length: t }, (_, i) => i + 1))
  if (!Number.isFinite(baseH) || !Number.isFinite(baseM)) return []
  return groups.map((g, i) => {
    const slot = Math.floor(i / t)
    const teeIdx = i % t
    const totalMin = baseH * 60 + baseM + slot * step
    const hh = Math.floor((totalMin % (24 * 60)) / 60)
    const mm = totalMin % 60
    const ampm = hh >= 12 ? 'PM' : 'AM'
    const h12 = ((hh + 11) % 12) + 1
    return { group: g, time: `${h12}:${String(mm).padStart(2, '0')} ${ampm}`, startHole: holes[teeIdx] }
  })
}

// 7. PLAY-WHEN-YOU-CAN: no fixed time — groups get an open window label. Stored
// as the group's assignment so the tee sheet shows "Open" instead of a clock.
export function openWindow(groups, label = 'Open') {
  return groups.map((g) => ({ group: g, time: label, startHole: null }))
}
